import cn from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import { Icon } from '../../../components';
import { EVISIT_STATUS } from '../../../config/constants';
import { buildPatientProfilePicUrl } from '../../../lib/util';
import { short } from '../common/formatting/date';
import { formatDistanceToNow, isBefore } from 'date-fns';

const EvisitStatusLabels = {
	[EVISIT_STATUS.IN_REVIEW]: 'In Review',
	[EVISIT_STATUS.READY_FOR_PROVIDER]: 'Submitted',
	[EVISIT_STATUS.CREATED]: 'Submitted',
	[EVISIT_STATUS.IN_PROGRESS]: 'In Progress',
	[EVISIT_STATUS.SCHEDULED]: 'Scheduled',
	[EVISIT_STATUS.SCHEDULED_PENDING]: 'Confirmed',
};

const ContentRow = ({ onClick, visit, isCurrentUser = false }) => {
	const handleClick = () => onClick(visit);
	const {
		patient,
		patient_dependent,
		currentProvider,
		evisit_status,
		status,
		appointment_schedule: appointment,
		start_dttm,
	} = visit;
	const isDependentEvisit =
		Boolean(visit.patientDependentId) && patient_dependent;
	const birthDttm = isDependentEvisit
		? short(patient_dependent.dependent_birth_dttm)
		: short(patient.birth_dttm);
	let genderLabel = '<Gender Unknown>';
	if (isDependentEvisit && patient_dependent.dependent_gender) {
		genderLabel =
			patient_dependent.dependent_gender === 'M' ? 'Male' : 'Female';
	} else if (patient.gender) {
		genderLabel = patient.gender === 'M' ? 'Male' : 'Female';
	}

	let statusLabel = '';
	if (evisit_status && EvisitStatusLabels[evisit_status]) {
		statusLabel = EvisitStatusLabels[evisit_status];
	} else if (status && EvisitStatusLabels[status]) {
		statusLabel = EvisitStatusLabels[status];
	}

	let waitingTime = '';
	if (appointment) {
		waitingTime = short(appointment.scheduled_start_dttm, false, "PP 'at' p");
	} else if (start_dttm) {
		waitingTime = formatDistanceToNow(new Date(start_dttm));
	}

	const isExpired =
		[EVISIT_STATUS.SCHEDULED, EVISIT_STATUS.SCHEDULED_PENDING].includes(
			evisit_status
		) &&
		appointment &&
		isBefore(new Date(appointment.scheduled_start_dttm), new Date());

	return (
		<tr
			className={cn('clickable', { danger: isExpired })}
			onClick={handleClick}
		>
			<td>
				<div className="media-box">
					<div className="pull-left">
						{isDependentEvisit ? (
							<div className="thumb48 bg-info img-rounded dependent-icon">
								<em className="fa fa-child fa-inverse fa-3x"></em>
							</div>
						) : (
							<img
								src={buildPatientProfilePicUrl(patient)}
								alt="Image"
								className="media-box-object img-responsive img-rounded thumb48"
							/>
						)}
					</div>
					<div className="media-box-body clearfix">
						<strong className="media-box-heading text-primary">
							{isDependentEvisit
								? `${patient_dependent.dependent_name} ${patient_dependent.dependent_name_last} (dependent of ${patient.name_first} ${patient.name_last})`
								: `${patient.name_first} ${patient.name_last}`}
						</strong>
						<p>
							{genderLabel} &middot; {birthDttm}
						</p>
					</div>
				</div>
			</td>
			<td>
				{visit.provider_visit_type && visit.provider_visit_type.visit_type}
			</td>
			<td className={cn('assigned-to', { warning: isCurrentUser })}>
				{currentProvider.name_first} {currentProvider.name_last}
			</td>
			<td>{statusLabel}</td>
			<td>{waitingTime}</td>
		</tr>
	);
};

ContentRow.propTypes = {
	onClick: PropTypes.func.isRequired,
	visit: PropTypes.object.isRequired,
	isCurrentUser: PropTypes.bool,
};

const TabContent = ({
	id,
	selected = false,
	visits = [],
	onRowClick,
	user = {},
	onChangeOrder,
	sortOrder = 'asc',
}) => {
	const handleSortChange = (event) => {
		event.preventDefault();
		onChangeOrder();
	};

	return (
		<div
			id={id}
			role="tabpanel"
			className={cn('tab-pane', { active: selected })}
		>
			<div className="table-responsive">
				<table className="table table-bordered table-hover">
					<thead>
						<tr>
							<th>Patient</th>
							<th>Reason for Visit</th>
							<th>Assigned To</th>
							<th>Status</th>
							<th>
								{id === 'scheduled-visits' ? (
									<React.Fragment>
										<a href="#" onClick={handleSortChange}>
											Date and Time of Appointment
										</a>{' '}
										<Icon
											name={
												sortOrder === 'asc'
													? 'fa-long-arrow-up'
													: 'fa-long-arrow-down'
											}
										/>
									</React.Fragment>
								) : (
									'Waiting Time'
								)}
							</th>
						</tr>
					</thead>
					<tbody>
						{visits.length == 0 ? (
							<tr>
								<td colSpan="6">No open visits found</td>
							</tr>
						) : (
							visits.map((visit) => (
								<ContentRow
									key={visit.id}
									visit={visit}
									onClick={onRowClick}
									isCurrentUser={visit.currentProvider.id === user.id}
								/>
							))
						)}
					</tbody>
				</table>
			</div>
		</div>
	);
};

TabContent.propTypes = {
	id: PropTypes.string.isRequired,
	selected: PropTypes.bool,
	visits: PropTypes.arrayOf(PropTypes.object),
	onRowClick: PropTypes.func,
	user: PropTypes.object,
	onChangeOrder: PropTypes.func,
	sortOrder: PropTypes.string,
};

export default TabContent;
